import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import {
  DEALS,
  DEALS_EYEBROW,
  DEALS_CURRENCY_NOTE,
  DEALS_LAST_REVIEWED,
} from "../../constant/deals";

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

// Where the exact model has no catalogue page, "View configuration" asks for
// the config sheet directly — the label stays honest either way.
const configLink = (deal) =>
  `https://wa.me/919755010858?text=${encodeURIComponent(
    `Hi, please send me the configuration for the ${deal.model} listed at ${inr.format(
      deal.price
    )} + GST.`
  )}`;

/**
 * "Our top selling servers" — the same three offers, prices and copy as the
 * section of that name on quantarock.in. These are the only published prices
 * on the shop, so the two sites must not drift apart.
 */
const DealsStrip = () => {
  if (!DEALS.length) return null;

  return (
    <section className="bg-[var(--website-primary-color)] py-8">
      <div className="mx-auto max-w-[88rem] px-4 sm:px-6">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-lg font-bold text-white sm:text-xl">
            Our top selling servers
          </h2>

          <Link
            to="/category/servers"
            className="inline-flex shrink-0 items-center gap-1 text-[0.82rem] font-semibold
                       text-[var(--website-secondary-color)] hover:underline"
          >
            All 21 servers
            <ArrowRight size={15} />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {DEALS.map((deal) => (
            <article
              key={deal.id}
              className="flex flex-col rounded-xl border border-white/15 bg-white/[0.04] p-5
                         transition-colors hover:border-[var(--website-secondary-color)]"
            >
              <span
                className="text-[0.62rem] font-bold uppercase tracking-[0.1em]
                           text-[var(--website-secondary-color)]"
              >
                {DEALS_EYEBROW}
              </span>

              <h3 className="mt-1.5 text-lg font-bold text-white">
                {deal.model}
              </h3>

              <p className="mt-2 text-3xl font-extrabold tracking-tight text-white">
                {inr.format(deal.price)}
              </p>
              <p className="mt-0.5 text-[0.72rem] text-[var(--website-text-color)]">
                {DEALS_CURRENCY_NOTE}
              </p>

              <ul className="mt-4 flex-1 space-y-1.5">
                {deal.highlights.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-2 text-[0.78rem] leading-snug
                               text-[var(--website-text-color)]"
                  >
                    <Check
                      size={14}
                      strokeWidth={3}
                      className="mt-0.5 shrink-0 text-[var(--website-secondary-color)]"
                    />
                    {point}
                  </li>
                ))}
              </ul>

              {deal.productSlug ? (
                <Link
                  to={`/category/${deal.productSlug}`}
                  className="mt-5 inline-flex items-center justify-center gap-1.5 rounded-lg
                             bg-[var(--website-secondary-color)] px-4 py-2.5 text-[0.82rem]
                             font-bold text-[var(--website-primary-color)]
                             transition-[filter] hover:brightness-105"
                >
                  View configuration
                  <ArrowRight size={15} />
                </Link>
              ) : (
                <a
                  href={configLink(deal)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center justify-center gap-1.5 rounded-lg
                             bg-[var(--website-secondary-color)] px-4 py-2.5 text-[0.82rem]
                             font-bold text-[var(--website-primary-color)]
                             transition-[filter] hover:brightness-105"
                >
                  View configuration
                  <ArrowRight size={15} />
                </a>
              )}
            </article>
          ))}
        </div>

        {/* Tailwind cannot apply an opacity modifier to an arbitrary var()
            colour — `text-[var(--x)]/75` compiles to nothing and leaves dark
            text on this navy panel. Use a separate opacity utility. */}
        <p className="mt-4 text-[0.7rem] leading-relaxed text-[var(--website-text-color)] opacity-75">
          Prices exclude GST and depend on final configuration and stock — your
          quotation confirms the figure. Reviewed {DEALS_LAST_REVIEWED}.
        </p>
      </div>
    </section>
  );
};

export default DealsStrip;
